import {emptyArray} from "./FunctionUtil";

export function getOrSet<Key, Value>(map: Map<Key, Value>, key: Key, defaultValue: () => Value): Value {
    const value = map.get(key);
    if (value !== undefined) {
        return value;
    } else {
        const newValue = defaultValue();
        map.set(key, newValue);
        return newValue;
    }
}

export function associateBy<Source, Key, Value = Source>(
    array: readonly Source[],
    key: (value: Source) => Key,
    transformValue?: (value: Source) => Value,
): Map<Key, Value> {
    if (transformValue) {
        return new Map(array.map(value => [key(value), transformValue(value)]));
    } else {
        return new Map(array.map(value => [key(value), value as any as Value]));
    }
}

export function groupBy<Source, Key, Value = Source>(
    array: readonly Source[],
    key: (value: Source) => Key,
    transformValue?: (value: Source) => Value,
): Map<Key, Value[]> {
    const map = new Map<Key, Value[]>();
    array.forEach(source => {
        const keyValue = key(source);
        const values = getOrSet(map, keyValue, emptyArray);
        values.push(transformValue ? transformValue(source) : source as any as Value);
    });
    return map;
}

export function mapValues<Key, Value, NewValue>(map: Map<Key, Value>, mapper: (value: Value) => NewValue): Map<Key, NewValue> {
    const newMap = new Map<Key, NewValue>();
    map.forEach((value, key) => {
        newMap.set(key, mapper(value));
    });
    return newMap;
}

export function invertMap<Key, Value>(map: Map<Key, Value>): Map<Value, Key> {
    return associateBy(
        [...map.entries()],
        ([_, value]) => value,
        ([key]) => key,
    )
}

export function invertMapToGroups<Key, Value>(map: Map<Key, Value>): Map<Value, Key[]> {
    return groupBy(
        [...map.entries()],
        ([_, value]) => value,
        ([key]) => key,
    );
}

export function mapRecordValues<Key extends string, Value, NewValue>(source: Record<Key, Value>, mapper: (value: Value) => NewValue): Record<Key, NewValue> {
    const result: Record<Key, NewValue> = {} as any;
    Object.keys(source).forEach(key => {
        result[key as Key] = mapper(source[key as Key]);
    });
    return result;
}